import { Box, Button, IconButton } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { FC, useState } from "react";
import { MoreHorizontal } from "react-feather";
import { EmployerModuleType } from "../../../models/accounts/employerModuleType";
import {
  SalarySacrifice,
  SalarySacrificeStatus
} from "../../../models/salarySacrifices/salarySacrifice";
import { formatCurrency, formatDate } from "../../../utils/formatters";
import { toTitleCase } from "../../../utils/toTitleCase";
import { useData } from "../../../utils/useData";
import { LoadingSpinner } from "../../shared/LoadingSpinner";
import { Table } from "../../shared/Table";
import { SalarySacrificeAssignmentDrawer } from "./SalarySacrificeAssignmentDrawer";
import { SalarySacrificeInfoDrawer } from "./SalarySacrificeInfoDrawer";
const SalarySacrificesTable: FC<{
  salarySacrifices: SalarySacrifice[];
  totalItems: number;
  page: number;
  pageSize: number;
  updatePage: (value: number) => void;
  updatePageSize: (value: number) => void;
  isLoading: boolean;
  refetch: () => void;
}> = ({
  salarySacrifices,
  totalItems,
  page,
  pageSize,
  updatePage,
  updatePageSize,
  isLoading,
  refetch
}) => {
  const { plans, employees } = useData();
  const [selectedSalarySacrifice, setSelectedSalarySacrifice] =
    useState<SalarySacrifice | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const renderSalarySacrificeMetadata = (salarySacrifice: SalarySacrifice) => {
    if (salarySacrifice.status === SalarySacrificeStatus.ERRORED) {
      return (
        <>
          <Button
            onClick={() => {
              setSelectedSalarySacrifice(salarySacrifice);
              setIsDrawerOpen(true);
            }}
            variant="outlined"
            size="small"
          >
            Assign
          </Button>
        </>
      );
    }
    return (
      <>
        <IconButton
          onClick={() => {
            setSelectedSalarySacrifice(salarySacrifice);
            setIsDrawerOpen(true);
          }}
        >
          <MoreHorizontal />
        </IconButton>
      </>
    );
  };

  const columns: ColumnDef<SalarySacrifice>[] = [
    {
      header: "Date",
      meta: {
        filterType: "dateRange"
      },
      enableColumnFilter: false,
      cell: ({ row }) => {
        const formattedDate = formatDate(row.original.createdAt);
        return <Box>{formattedDate}</Box>;
      }
    },
    {
      header: "Amount",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const formattedAmount = formatCurrency(row.original.amount.amount);
        return <Box>{formattedAmount}</Box>;
      }
    },
    {
      header: "Account ID",
      cell: ({ row }) => <Box>{row.original.code ?? "-"}</Box>,
      enableColumnFilter: false
    },
    {
      header: "Employee ID",
      cell: ({ row }) => <Box>{row.original.reference ?? "-"}</Box>,
      enableColumnFilter: false
    },
    {
      header: "Status",
      cell: ({ row }: { row: { original: SalarySacrifice } }) => {
        return <Box>{toTitleCase(row.original.status)}</Box>;
      },
      enableColumnFilter: false
    },
    {
      header: "Info",
      cell: ({ row }: { row: { original: SalarySacrifice } }) => {
        return renderSalarySacrificeMetadata(row.original);
      }
    }
  ];

  if (isLoading || plans.isLoading) return <LoadingSpinner />;

  return (
    <>
      <Table<SalarySacrifice>
        data={salarySacrifices}
        totalItems={totalItems}
        columns={columns}
        isServerSidePagination={true}
        paginationPage={page}
        paginationPageSize={pageSize}
        onPageChange={updatePage}
        onPageSizeChange={updatePageSize}
        displaySearch={true}
        displayFilters={true}
      />

      {selectedSalarySacrifice?.status === SalarySacrificeStatus.ASSIGNED && (
        <SalarySacrificeInfoDrawer
          salarySacrifice={selectedSalarySacrifice}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
        />
      )}

      {selectedSalarySacrifice?.status === SalarySacrificeStatus.ERRORED && (
        <SalarySacrificeAssignmentDrawer
          salarySacrifice={selectedSalarySacrifice}
          plans={
            plans.data?.items.filter(
              (plan) =>
                plan.employerModuleOptions.employerModuleType ===
                EmployerModuleType.PUBLIC_TRANSPORT
            ) ?? []
          }
          employees={
            employees.data?.items.filter(
              (employee) =>
                employee.customerEmployeeId !== null &&
                employee.customerEmployeeId !== "" &&
                employee.customerEmployeeId !== undefined &&
                employee.customerEmployeeId.length > 0
            ) ?? []
          }
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          refetch={refetch}
        />
      )}
    </>
  );
};

export { SalarySacrificesTable };
